// src/components/admin/tenant/TenantForm.tsx
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Tenant, fetchTenantById, createTenant, updateTenant } from './TenantApi';

export type FormMode = 'create' | 'edit' | 'view';

interface Props {
  mode: FormMode;
}

const defaultTenant: Tenant = {
  id: '',
  name: '',
  subdomain: '',
  schemaName: '',
  contactEmail: '',
  mobileNo: '',
  whatsAppNo: '',
  status: "ACTIVE",
  comment: '',
  createdAt: '',
  updatedAt: '',
};

const TenantForm: React.FC<Props> = ({ mode }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [tenant, setTenant] = useState<Tenant>(defaultTenant);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const isReadOnly = mode === 'view';

  useEffect(() => {
    if ((mode === 'view' || mode === 'edit') && id) {
      loadTenantById(id);
    } else if (mode === 'create') {
      setTenant(defaultTenant);
    }
  }, [id, mode]);

  const loadTenantById = async (tenantId: string) => {
    setLoading(true);
    try {
      const response = await fetchTenantById(tenantId);
      setTenant(response.data);
    } catch (err) {
      setError('Failed to fetch tenant');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setTenant((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (mode === 'edit' && id) {
        await updateTenant(id, tenant);
      } else if (mode === 'create') {
        await createTenant(tenant);
      }
      navigate('/admin/tenants');
    } catch (err) {
      setError('Failed to save tenant');
    }
  };

  const handleReset = () => {
    setTenant(defaultTenant);
  };

  const renderInput = (label: string, field: keyof Tenant, type = 'text') => (
    <div className="mb-4">
      <label className="block text-sm font-semibold mb-1">{label}</label>
      <input
        type={type}
        name={field}
        value={tenant[field] || ''}
        onChange={handleChange}
        readOnly={isReadOnly}
        className={`border px-3 py-2 rounded w-full ${isReadOnly ? 'bg-gray-100' : ''}`}
      />
    </div>
  );

  if (loading) return <div className="p-6">Loading...</div>;
  if (error) return <div className="p-6 text-red-600">{error}</div>;

  return (
    <div className="p-6  mx-auto bg-white  w-full   shadow rounded">
      {/* <h2 className="text-2xl font-semibold mb-4">
        {mode === 'create' ? 'Create Tenant' : mode === 'edit' ? 'Edit Tenant' : 'View Tenant'}
      </h2> */}

      <form onSubmit={handleSubmit}>
        {renderInput('Name', 'name')}
        {renderInput('Subdomain', 'subdomain')}
        {renderInput('Schema Name', 'schemaName')}
        {renderInput('Email', 'contactEmail')}
        {renderInput('Mobile No', 'mobileNo')}
        {renderInput('WhatsApp No', 'whatsAppNo')}
        {renderInput('Status', 'status')}
        {renderInput('Comment', 'comment')}

        <div className="mt-6 flex gap-4">
          {mode !== 'view' && (
            <button
              type="submit"
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
              {mode === 'create' ? 'Create' : 'Update'}
            </button>
          )}

          {mode === 'create' && (
            <button
              type="button"
              onClick={handleReset}
              className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
            >
              Reset
            </button>
          )}

          <button
            type="button"
            onClick={() => navigate('/admin/tenants')}
            className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
          >
            Back
          </button>
        </div>
      </form>
    </div>
  );
};

export default TenantForm;